/**
 * Assignments audit (Admin > Enablement > Assignments).
 *
 * Read-only view of who has been assigned which journey map, by whom, whether it is Required or
 * Optional, when it is due and whether the assignee was notified. Individual assignments and
 * group/role targets are shown together. Assigning itself happens on the Enablement journey page.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { Alert, Box, Chip, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { fetchAllItems } from '../../../support/queries';
import {
  AuditKind,
  AuditRow,
  buildAuditRows,
  filterAuditRows,
  RequiredFilter,
} from '../../../support/journeyMapAssignmentAudit';

const asList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const nameMap = (rows: any[]) =>
  Object.fromEntries(rows.map((g) => [String(g.id), String(g.name ?? `Group ${g.id}`)]));

const KINDS: Array<AuditKind | 'all'> = ['all', 'User', 'User group', 'Azure AD group', 'Other target'];

const COLUMNS: GridColDef<AuditRow>[] = [
  { field: 'journeyMapName', headerName: 'Journey map', flex: 1.4, minWidth: 200 },
  { field: 'kind', headerName: 'Type', width: 130 },
  { field: 'assignee', headerName: 'Assigned to', flex: 1.2, minWidth: 200 },
  { field: 'assignedBy', headerName: 'Assigned by', flex: 1, minWidth: 180 },
  {
    field: 'isRequired',
    headerName: 'Required',
    width: 120,
    renderCell: ({ value }) => (
      <Chip size="small" color={value ? 'primary' : 'default'} label={value ? 'Required' : 'Optional'} />
    ),
  },
  { field: 'dueDate', headerName: 'Due', width: 120 },
  { field: 'status', headerName: 'Status', width: 140 },
  { field: 'notifiedAt', headerName: 'Notified', width: 120 },
];

const JourneyMapAssignmentsAdminPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [journeyMapId, setJourneyMapId] = useState<number | 'all'>('all');
  const [required, setRequired] = useState<RequiredFilter>('all');
  const [kind, setKind] = useState<AuditKind | 'all'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-journey-map-assignment-audit'],
    queryFn: async () => {
      const [assignments, targets, userGroups, azureGroups] = await Promise.all([
        fetchAllItems('db/journey-map-assignment/', {}),
        fetchAllItems('db/journey-map-assignment-target/', {}),
        fetchAllItems('db/user-group/', {}),
        fetchAllItems('db/azure-ad-group/', {}),
      ]);
      return buildAuditRows({
        assignments: asList(assignments),
        targets: asList(targets),
        userGroupNames: nameMap(asList(userGroups)),
        azureGroupNames: nameMap(asList(azureGroups)),
      });
    },
    staleTime: 30_000,
  });

  const rows = useMemo<AuditRow[]>(() => data ?? [], [data]);

  const journeyOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (r.journeyMapId != null) seen.set(r.journeyMapId, r.journeyMapName);
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = useMemo(
    () => filterAuditRows(rows, { search, journeyMapId, required, kind }),
    [rows, search, journeyMapId, required, kind]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load assignments.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Journey, person, group or assigner"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Journey map"
          value={journeyMapId}
          onChange={(e) => setJourneyMapId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="all">All journey maps</MenuItem>
          {journeyOptions.map((j) => (
            <MenuItem key={j.id} value={j.id}>
              {j.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Required"
          value={required}
          onChange={(e) => setRequired(e.target.value as RequiredFilter)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">Required and Optional</MenuItem>
          <MenuItem value="required">Required</MenuItem>
          <MenuItem value="optional">Optional</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          value={kind}
          onChange={(e) => setKind(e.target.value as AuditKind | 'all')}
          sx={{ minWidth: 170 }}
        >
          {KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {k === 'all' ? 'All types' : k}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ height: 560 }}>
        <DataGrid
          rows={filtered}
          columns={COLUMNS}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Box>
    </Stack>
  );
};

export default JourneyMapAssignmentsAdminPage;
